// Custom hook: useFetchWithAuth
// Reads token + refreshToken from AuthContext; returns fetchWithAuth + fetchWithAuthFormData.
// Components import this hook instead of receiving fetchWithAuth as a prop.

import React, { useCallback } from 'react';
import { API_BASE } from '../config';
import { AuthContext } from '../App';
import { getOrCreateDeviceId } from '../utils/auth-helpers';

/**
 * Custom hook providing fetchWithAuth and fetchWithAuthFormData.
 * Reads token and refreshToken from AuthContext.
 *
 * Usage:
 *   const { fetchWithAuth, fetchWithAuthFormData } = useFetchWithAuth();
 */
export function useFetchWithAuth() {
  const { token, refreshToken } = React.useContext(AuthContext);

  const fetchWithAuth = useCallback(async (url, options = {}) => {
    const doFetch = (authToken) => fetch(url, {
      ...options,
      headers: {
        'Authorization': `Bearer ${authToken}`,
        'Content-Type': 'application/json',
        'X-Device-Id': getOrCreateDeviceId(),
        ...options.headers
      }
    });

    // Always read from localStorage to avoid stale closure in setInterval pollers
    let response = await doFetch(localStorage.getItem('token'));

    if (response.status === 401) {
      const refreshed = await refreshToken();
      if (refreshed) {
        const newToken = localStorage.getItem('token');
        response = await doFetch(newToken);
      }
    }

    return response;
  }, [refreshToken]);

  const fetchWithAuthFormData = useCallback(async (url, formData) => {
    const doFetch = (authToken) => fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${authToken}`,
        'X-Device-Id': getOrCreateDeviceId()
      },
      body: formData
    });

    // Always read from localStorage to avoid stale closure in setInterval pollers
    let response = await doFetch(localStorage.getItem('token'));

    if (response.status === 401) {
      const refreshed = await refreshToken();
      if (refreshed) {
        const newToken = localStorage.getItem('token');
        response = await doFetch(newToken);
      }
    }

    return response;
  }, [refreshToken]);

  return { fetchWithAuth, fetchWithAuthFormData, token, API_BASE };
}
